import { ScaleLinear, drag, select } from "d3";
import { clamp } from "lodash";
import { RefObject, useEffect, useState } from "react";

import { usePlayContext } from "../../../pages/Play/play-context";
import { Action, DragGroupElement } from "../../../types";
import { useBarConstraints } from "./use-bar-constraints";

export type UseMoveActionBarProps = {
  action: Action;
  x: number;
  scale: ScaleLinear<number, number>;
  actionBarRef: RefObject<SVGGElement>;
  canEdit: boolean;
};

export const useMoveActionBar = ({ action, x, scale, actionBarRef, canEdit }: UseMoveActionBarProps) => {
  const { id: actionId, startTime, endTime } = action;
  const { frameStencilsApi, playStateApi } = usePlayContext();
  const { updateMovementTime } = frameStencilsApi;
  const { activeFrameStencils } = playStateApi;

  const { nextMinTime, prevMaxTime } = useBarConstraints({ action, frameStencils: activeFrameStencils, scale });

  // position of action bar only controlled by external props, used to calculate x offset
  const [staticX, setStaticX] = useState<number>(x);
  // position of action bar that is controlled both by external props and internal drag events
  const [tempX, setTempX] = useState<number>(x);

  useEffect(() => {
    setStaticX(x);
    setTempX(x);
  }, [x]);

  useEffect(() => {
    const g = actionBarRef.current;

    const domain = scale.domain();
    const range = scale.range();
    const actionTime = endTime - startTime;
    const actionLength = scale(actionTime);

    if (g && canEdit) {
      select<SVGGElement, undefined>(g).call(
        drag<SVGGElement, undefined>()
          // on drag, calculate new pos and set the temp position
          .on("drag", function (event: DragGroupElement) {
            const { x, subject } = event;
            const { x: subjectX } = subject;

            const newX = x - (subjectX - staticX);

            const minTimeClamp = Math.max(scale(prevMaxTime), range[0] || 0);
            const maxTimeClamp = Math.min(scale(nextMinTime), range[1] || 0);

            const newXClamped = clamp(newX, minTimeClamp, maxTimeClamp - actionLength);

            setTempX(newXClamped);
          })
          // on drag end, calculate delta position and update position in data state
          .on("end", function (event: DragGroupElement) {
            const { x, subject } = event;
            const { x: subjectX } = subject;

            const deltaX = x - subjectX;

            const deltaTime = scale.invert(deltaX);

            const startMinTimeClamp = Math.max(prevMaxTime, domain[0] || 0);
            const endMaxTimeClamp = Math.min(nextMinTime, domain[1] || 0);

            const startTimeClamped = clamp(startTime + deltaTime, startMinTimeClamp, endMaxTimeClamp - actionTime);
            const endTimeClamped = clamp(endTime + deltaTime, startMinTimeClamp + actionTime, endMaxTimeClamp);

            updateMovementTime({ actionId, startTime: startTimeClamped, endTime: endTimeClamped });
          })
      );
    }

    return () => {
      if (g) select<SVGGElement, undefined>(g).on("mousedown.drag", null);
    };
  }, [
    actionBarRef,
    canEdit,
    scale,
    staticX,
    actionId,
    startTime,
    endTime,
    updateMovementTime,
    prevMaxTime,
    nextMinTime,
  ]);

  return { tempX };
};
